/**
 * Content filtering utilities for SFW (Safe For Work) checking.
 */

// Basic list of inappropriate words/phrases (matches backend)
const INAPPROPRIATE_WORDS = [
  // Profanity and offensive language
  "fuck", "shit", "damn", "hell", "bitch", "ass", "asshole", "bastard",
  "crap", "piss", "dick", "cock", "pussy", "whore", "slut", "fag",
  "nigger", "nigga", "kike", "chink", "spic", "wetback", "towelhead",
  
  // Sexual content
  "sex", "porn", "pornography", "xxx", "nude", "naked", "masturbat",
  "orgasm", "penis", "vagina", "breast", "boob", "tit", "dildo",
  
  // Violence/threats
  "kill", "murder", "suicide", "bomb", "terrorist", "rape", "violence",
  "weapon", "gun", "knife", "blood", "death", "die", "dead",
  
  // Drugs/alcohol
  "cocaine", "heroin", "marijuana", "weed", "drug", "alcohol", "drunk",
  "high", "stoned", "meth", "crack", "lsd", "ecstasy",
  
  // Hate speech
  "hate", "racist", "nazi", "hitler", "kkk", "white power", "black power",
  "supremacist", "genocide", "holocaust",
  
  // Other inappropriate content
  "scam", "fraud", "steal", "theft", "illegal", "hack", "virus",
  "malware", "phishing", "spam"
];

export const isSFWContent = (text: string): boolean => {
  /**
   * Check if the given text is Safe For Work (SFW).
   * 
   * @param text - The text to check
   * @returns True if the text is SFW, False otherwise
   */
  if (!text) {
    return true;
  }
  
  // Convert to lowercase for case-insensitive checking
  const textLower = text.toLowerCase();
  
  // Remove common punctuation and split into words
  const words = textLower.match(/\b\w+\b/g) || [];
  
  // Check each word against the inappropriate words list
  for (const word of words) {
    if (INAPPROPRIATE_WORDS.includes(word)) {
      return false;
    }
  }
  
  // Check for partial matches (e.g., "fucking" contains "fuck")
  for (const inappropriateWord of INAPPROPRIATE_WORDS) {
    if (textLower.includes(inappropriateWord)) {
      return false;
    }
  }
  
  return true;
};

export const getInappropriateWordsFound = (text: string): string[] => {
  /**
   * Get a list of inappropriate words found in the text.
   * 
   * @param text - The text to check
   * @returns List of inappropriate words found
   */
  if (!text) {
    return [];
  }
  
  const foundWords: string[] = [];
  const textLower = text.toLowerCase();
  
  // Check for exact word matches
  const words = textLower.match(/\b\w+\b/g) || [];
  
  for (const word of words) {
    if (INAPPROPRIATE_WORDS.includes(word) && !foundWords.includes(word)) {
      foundWords.push(word);
    }
  }
  
  // Check for partial matches
  for (const inappropriateWord of INAPPROPRIATE_WORDS) {
    if (textLower.includes(inappropriateWord) && !foundWords.includes(inappropriateWord)) {
      foundWords.push(inappropriateWord);
    }
  }
  
  return foundWords;
};

export const containsCoordinateKeywords = (text: string, gridWords?: { x_axis?: Record<number, string>, y_axis?: Record<number, string> }): boolean => {
  /**
   * Check if the text contains any coordinate keywords from the game grid.
   * 
   * @param text - The text to check
   * @param gridWords - Dictionary containing x_axis and y_axis words from the game grid
   * @returns True if coordinate keywords are found, False otherwise
   */
  if (!text || !gridWords) {
    return false;
  }
  
  const textLower = text.toLowerCase();
  
  // Check X-axis words
  if (gridWords.x_axis) {
    for (const word of Object.values(gridWords.x_axis)) {
      if (textLower.includes(word.toLowerCase())) {
        return true;
      }
    }
  }
  
  // Check Y-axis words
  if (gridWords.y_axis) {
    for (const word of Object.values(gridWords.y_axis)) {
      if (textLower.includes(word.toLowerCase())) {
        return true;
      }
    }
  }
  
  return false;
};

export const getCoordinateKeywordsFound = (text: string, gridWords?: { x_axis?: Record<number, string>, y_axis?: Record<number, string> }): string[] => {
  /**
   * Get a list of coordinate keywords found in the text.
   * 
   * @param text - The text to check
   * @param gridWords - Dictionary containing x_axis and y_axis words from the game grid
   * @returns List of coordinate keywords found
   */
  if (!text || !gridWords) {
    return [];
  }
  
  const foundWords: string[] = [];
  const textLower = text.toLowerCase();
  
  // Check X-axis words
  if (gridWords.x_axis) {
    for (const word of Object.values(gridWords.x_axis)) {
      if (textLower.includes(word.toLowerCase()) && !foundWords.includes(word)) {
        foundWords.push(word);
      }
    }
  }
  
  // Check Y-axis words
  if (gridWords.y_axis) {
    for (const word of Object.values(gridWords.y_axis)) {
      if (textLower.includes(word.toLowerCase()) && !foundWords.includes(word)) {
        foundWords.push(word);
      }
    }
  }
  
  return foundWords;
};
